import {ApiClient} from '@hconnect/apiclient'
import {HttpError} from '@hconnect/common/types'
import {useQuery, UseQueryOptions, keepPreviousData} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import moment, {Moment} from 'moment'

import {createRetryFunction} from '../../../shared/helpers/queryHelpers'
import {NormalizeType, SignalGraphData} from '../types/sensor.types'

import {usePlantUpmId} from './usePlantUpmId'

export const QueryKey = 'signal-plot-data'

export const useGetSignalPlotData = ({
  sensorIds,
  deltaHours,
  timeFrom,
  timeTo,
  normalized = false,
  options
}: {
  sensorIds: string[]
  deltaHours?: number
  timeFrom?: Moment
  timeTo?: Moment
  normalized?: boolean
  options?: UseQueryOptions<SignalGraphData, AxiosError<HttpError>>
}) => {
  const plantUpmId = usePlantUpmId()

  const timeFromRoundedMinutes = timeFrom
    ? timeFrom.clone().startOf('minute')
    : moment()
        .startOf('minute')
        .subtract(deltaHours ?? 24, 'hours')

  const timeToRoundedMinutes = timeTo
    ? timeTo.clone().startOf('minute')
    : moment().startOf('minute')

  const timeFromIso = timeFromRoundedMinutes.toISOString()
  const timeToIso = timeToRoundedMinutes.toISOString()

  return useQuery<SignalGraphData, AxiosError<HttpError>>({
    queryKey: [QueryKey, plantUpmId, sensorIds, timeFromIso, timeToIso, normalized],
    queryFn: async () => {
      const path = `/kms/${plantUpmId}/signal/plot-data`
      return (
        await ApiClient.axiosInstance.get<SignalGraphData>(path, {
          params: {
            normalizeType: NormalizeType.NONE,
            localNames: sensorIds,
            timeTo: timeToIso,
            timeFrom: timeFromIso
          }
        })
      ).data
    },
    // these default values will get moved to the api provider soon
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    throwOnError: false,
    retry: createRetryFunction(3),
    retryDelay: 1000 * 3,
    placeholderData: keepPreviousData,
    enabled: plantUpmId !== undefined && sensorIds.length > 0,
    // allow the callee to override the default options
    ...options
  })
}
